import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiEdit2 } from 'react-icons/fi';
import { useAuth } from '../../Context/AuthContext';
import {
  FaStar, FaSpinner, FaCheck, FaClock, FaFire, FaTrophy,
  FaShieldAlt, FaLightbulb, FaRocket, FaMedal, FaComments, FaBolt,
  FaWallet, FaRegCopy
} from 'react-icons/fa';
import PaymentAccountsManager from '../PaymentAccounts/PaymentAccountsManager';
import RedeemInvite from '../SellerInvite/RedeemInvite';
import VerifyPhonePrompt from '../VerifyEmail/VerifyPhonePrompt';
import SignInMethods from './SignInMethods';
import EditProfile from '../EditProfile/EditProfile';
import Identicon from '../common/Identicon';
import './UserProfile.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
// La verificación por teléfono requiere proveedor SMS (Twilio) → gateada por flag.
const SMS_ENABLED = !!process.env.REACT_APP_SMS_ENABLED;

// Identidad por wallet: muestra la dirección (autocustodia) truncada + copiar.
function WalletAddressChip({ address }) {
  const [copied, setCopied] = useState(false);
  const short = `${address.slice(0, 6)}…${address.slice(-4)}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(address); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {}
  };
  return (
    <button
      type="button"
      className="up-wallet-chip"
      onClick={copy}
      title={`${address} — tu plata, tu llave (autocustodia)`}
    >
      <FaWallet size={11} />
      <span className="up-wallet-addr">{short}</span>
      {copied ? <FaCheck size={11} /> : <FaRegCopy size={11} />}
    </button>
  );
}

export default function UserProfile({ userId: paramUserId }) {
  const { user: currentUser } = useAuth();
  const userId = paramUserId || currentUser?.id;

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await fetch(`${API_URL}/api/users/${userId}/profile`);
        if (!response.ok) {
          throw new Error('Usuario no encontrado');
        }
        const data = await response.json();
        setUser(data);
      } catch (err) {
        setError(err.message || 'Error al cargar el perfil');
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchUserProfile();
    }
  }, [userId]);

  if (loading) {
    return (
      <div className="up-container">
        <div className="up-loading">
          <FaSpinner className="up-spinner" size={40} />
          <p>Cargando perfil...</p>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="up-container">
        <div className="up-error" style={{ textAlign: 'center' }}>
          <p style={{ fontWeight: 700 }}><FaShieldAlt style={{ verticalAlign: -2, marginRight: 6, color: 'var(--fin-down)' }} /> {error || 'No se pudo cargar el perfil'}</p>
          <p style={{ color: 'var(--mk-text-tertiary)', fontSize: '0.84rem', marginTop: 8 }}>
            ID buscado: <code>{String(userId)}</code>
          </p>
          <p style={{ color: 'var(--mk-text-tertiary)', fontSize: '0.84rem', marginTop: 8, maxWidth: 460, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.5 }}>
            Tu sesión quedó vieja (no coincide con la base actual). <strong>Cerrá sesión y volvé a entrar</strong> — eso refresca tu cuenta. Si al re-loguear te dice que no existe, tu usuario se borró al resetear la DB y hay que registrarse de nuevo.
          </p>
        </div>
      </div>
    );
  }

  const avgRating = user.averageRating || 0;
  const totalReviews = user.totalReviews || 0;
  const completionRate = user.completionRate || 0;
  const responseTime = user.responseTime || 'N/A';
  const reviews = user.reviews || [];
  // ¿Es mi propio perfil? (sin :userId en la ruta, o coincide con el usuario en sesión)
  const isOwn = !!currentUser && !!user && String(currentUser.id) === String(user.id);

  const getRatingColor = (rating) => {
    if (rating >= 4.5) return '#00d412';
    if (rating >= 3.5) return '#0091ea';
    if (rating >= 2.5) return '#f0b90b';
    return '#f4511e';
  };

  const getRatingLabel = (rating) => {
    if (rating >= 4.8) return 'Excelente';
    if (rating >= 4) return 'Muy bueno';
    if (rating >= 3) return 'Bueno';
    if (rating >= 2) return 'Regular';
    return 'Pobre';
  };

  const calculateCategoryAverages = (reviews) => {
    if (!reviews || reviews.length === 0) return null;

    const categories = {
      communication: [],
      speed: [],
      accuracy: []
    };

    reviews.forEach(review => {
      if (review.communicationRating) categories.communication.push(review.communicationRating);
      if (review.speedRating) categories.speed.push(review.speedRating);
      if (review.accuracyRating) categories.accuracy.push(review.accuracyRating);
    });

    return {
      communication: categories.communication.length > 0
        ? (categories.communication.reduce((a, b) => a + b) / categories.communication.length).toFixed(1)
        : 0,
      speed: categories.speed.length > 0
        ? (categories.speed.reduce((a, b) => a + b) / categories.speed.length).toFixed(1)
        : 0,
      accuracy: categories.accuracy.length > 0
        ? (categories.accuracy.reduce((a, b) => a + b) / categories.accuracy.length).toFixed(1)
        : 0
    };
  };

  const calculateTrustScore = (user) => {
    const ratingScore = (avgRating / 5) * 40;
    const completionScore = (completionRate / 100) * 35;
    const reviewScore = Math.min((totalReviews / 50) * 25, 25);
    return Math.round(ratingScore + completionScore + reviewScore);
  };

  const getBadges = (user) => {
    const badges = [];
    const completedTrades = user.completedTrades || 0;

    if (user.walletAddress) badges.push({ label: 'Autocustodia', icon: FaWallet, color: '#00d412' });
    if (avgRating >= 4.8) badges.push({ label: 'Top Trader', icon: FaTrophy, color: '#f0b90b' });
    if (completedTrades >= 100) badges.push({ label: '100+ Trades', icon: FaMedal, color: '#00d412' });
    if (completedTrades >= 50) badges.push({ label: '50+ Trades', icon: FaRocket, color: '#0091ea' });
    if (totalReviews >= 20) badges.push({ label: 'Verified', icon: FaShieldAlt, color: '#00d412' });
    if (completionRate >= 99) badges.push({ label: 'Trusted', icon: FaCheck, color: '#00b248' });

    return badges;
  };

  const getSortedReviews = () => {
    if (!reviews) return [];
    const sorted = [...reviews];

    switch (sortBy) {
      case 'rating':
        return sorted.sort((a, b) => b.rating - a.rating);
      case 'oldest':
        return sorted.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      case 'recent':
      default:
        return sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }
  };

  return (
    <div className="up-container">
      {/* Barra de acciones del dueño: editar perfil (cuenta + tienda en un solo lugar) */}
      {isOwn && (
        <div className="up-owner-bar">
          <span className="up-owner-hint">Este es tu perfil</span>
          <button className="up-edit-btn" onClick={() => setEditOpen(true)}>
            <FiEdit2 size={14} /> Editar perfil
          </button>
        </div>
      )}

      {/* Formas de entrar: email + wallet unificados (una cuenta, varias llaves) */}
      {isOwn && <SignInMethods />}

      {/* CTA → tienda pública (storefront): banner, titular, horario, QR, link propio */}
      {user?.username && (
        <Link to={`/profile/${user.username}`} className="up-storefront-cta">
          <span className="up-storefront-cta-info">
            <strong>🏪 Tu tienda de comerciante</strong>
            <span>Así te ven tus clientes: banner, titular, horario y ofertas destacadas. Compartila con QR y tu link propio.</span>
          </span>
          <span className="up-storefront-cta-btn">Ver mi tienda pública →</span>
        </Link>
      )}

      {/* Cuentas de cobro guardadas (presets para enviar en el chat) */}
      <div style={{ marginBottom: '1.25rem' }}>
        <PaymentAccountsManager />
      </div>

      {/* Verificación de teléfono (KYC) — se auto-oculta si no aplica */}
      {SMS_ENABLED && (
        <div style={{ marginBottom: '1.25rem' }}>
          <VerifyPhonePrompt />
        </div>
      )}

      {/* Canjear código de invitación → vendedor verificado (se auto-oculta si ya lo es) */}
      <div style={{ marginBottom: '1.25rem' }}>
        <RedeemInvite />
      </div>

      {/* Profile Header */}
      <section className="up-header">
        <div className="up-avatar-section">
          <Identicon
            seed={user.walletAddress || user.username || user.name}
            src={user.avatar}
            size={104}
            className="up-avatar"
            alt={user.name}
          />
        </div>

        <div className="up-info">
          <div className="up-name-section">
            <h1 className="up-name">@{user.name}</h1>
            {user.walletAddress && <WalletAddressChip address={user.walletAddress} />}
            <div className="up-badges">
              {getBadges(user).map((badge, idx) => (
                <div key={idx} className="up-badge fin-chip" style={{ '--badge-color': badge.color }}>
                  <badge.icon size={12} />
                  <span>{badge.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Titular + bio del storefront (se auto-ocultan si están vacíos) */}
          {user.headline && <p className="up-headline">{user.headline}</p>}
          {user.bio && <p className="up-bio">{user.bio}</p>}

          <div className="up-rating-block">
            <div className="up-stars">
              {[1, 2, 3, 4, 5].map(i => (
                <FaStar
                  key={i}
                  size={22}
                  color={i <= Math.round(avgRating) ? getRatingColor(avgRating) : 'var(--mk-border)'}
                  style={{ marginRight: '4px' }}
                />
              ))}
            </div>
            <div className="up-rating-info">
              <span className="up-rating-value fin-num" style={{ color: getRatingColor(avgRating) }}>
                {avgRating.toFixed(1)}
              </span>
              <span className="up-rating-label">{getRatingLabel(avgRating)}</span>
              <span className="up-review-count">(<span className="fin-num">{totalReviews}</span> reseña{totalReviews !== 1 ? 's' : ''})</span>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Score Section */}
      <section className="up-trust-section">
        <div className="up-trust-card">
          <div className="up-trust-header">
            <FaShieldAlt size={22} style={{ color: 'var(--fin-live)' }} />
            <h3 className="up-trust-title">Puntuación de Confianza</h3>
          </div>
          <div className="up-trust-score">
            <div className="up-trust-value fin-num">{calculateTrustScore(user)}</div>
            <div className="up-trust-bar">
              <div className="up-trust-fill" style={{ width: `${calculateTrustScore(user)}%` }}></div>
            </div>
            <span className="up-trust-label fin-label">Basado en: Rating, Completación, Reseñas</span>
          </div>
        </div>
      </section>

      {/* Stats Cards */}
      <section className="up-stats">
        <div className="up-stat-card">
          <div className="up-stat-icon" style={{ color: 'var(--fin-up)' }}>
            <FaCheck size={20} />
          </div>
          <div className="up-stat-content">
            <span className="up-stat-label fin-label">Completados</span>
            <span className="up-stat-value fin-num">{user.completedTrades || 0}</span>
          </div>
        </div>

        <div className="up-stat-card">
          <div className="up-stat-icon" style={{ color: 'var(--fin-buy)' }}>
            <FaTrophy size={20} />
          </div>
          <div className="up-stat-content">
            <span className="up-stat-label fin-label">Total trades</span>
            <span className="up-stat-value fin-num">{user.totalTrades || 0}</span>
          </div>
        </div>

        <div className="up-stat-card">
          <div className="up-stat-icon" style={{ color: 'var(--fin-gold)' }}>
            <FaFire size={20} />
          </div>
          <div className="up-stat-content">
            <span className="up-stat-label fin-label">Tasa de completación</span>
            <span className="up-stat-value fin-num">{completionRate}%</span>
          </div>
        </div>

        <div className="up-stat-card">
          <div className="up-stat-icon" style={{ color: 'var(--mk-text-secondary)' }}>
            <FaClock size={20} />
          </div>
          <div className="up-stat-content">
            <span className="up-stat-label fin-label">Tiempo promedio</span>
            <span className="up-stat-value fin-num">{responseTime}</span>
          </div>
        </div>
      </section>

      {/* Category Averages Section */}
      {calculateCategoryAverages(reviews) && (
        <section className="up-categories-section">
          <h3 className="up-categories-title">Fortalezas Evaluadas</h3>
          <div className="up-categories-grid">
            <div className="up-category-card">
              <div className="up-category-header">
                <FaComments size={18} style={{ color: '#0091ea' }} />
                <span className="up-category-label fin-label">Comunicación</span>
              </div>
              <div className="up-category-rating">
                {[1, 2, 3, 4, 5].map(i => (
                  <FaStar
                    key={i}
                    size={14}
                    color={i <= Math.round(calculateCategoryAverages(reviews).communication) ? '#0091ea' : 'var(--mk-border)'}
                  />
                ))}
              </div>
              <span className="up-category-score fin-num">{calculateCategoryAverages(reviews).communication}/5</span>
            </div>

            <div className="up-category-card">
              <div className="up-category-header">
                <FaBolt size={18} style={{ color: '#f0b90b' }} />
                <span className="up-category-label fin-label">Rapidez</span>
              </div>
              <div className="up-category-rating">
                {[1, 2, 3, 4, 5].map(i => (
                  <FaStar
                    key={i}
                    size={14}
                    color={i <= Math.round(calculateCategoryAverages(reviews).speed) ? '#f0b90b' : 'var(--mk-border)'}
                  />
                ))}
              </div>
              <span className="up-category-score fin-num">{calculateCategoryAverages(reviews).speed}/5</span>
            </div>

            <div className="up-category-card">
              <div className="up-category-header">
                <FaLightbulb size={18} style={{ color: 'var(--fin-live)' }} />
                <span className="up-category-label fin-label">Precisión</span>
              </div>
              <div className="up-category-rating">
                {[1, 2, 3, 4, 5].map(i => (
                  <FaStar
                    key={i}
                    size={14}
                    color={i <= Math.round(calculateCategoryAverages(reviews).accuracy) ? 'var(--fin-live)' : 'var(--mk-border)'}
                  />
                ))}
              </div>
              <span className="up-category-score fin-num">{calculateCategoryAverages(reviews).accuracy}/5</span>
            </div>
          </div>
        </section>
      )}

      {/* Reviews Section */}
      <section className="up-reviews">
        <div className="up-reviews-header">
          <div className="up-reviews-title-section">
            <h2 className="up-reviews-title">Reseñas Recibidas</h2>
            <span className="up-reviews-count fin-num">{totalReviews}</span>
          </div>
          {reviews.length > 0 && (
            <div className="up-reviews-sort">
              <label htmlFor="sort-select" className="up-sort-label">Ordenar:</label>
              <select
                id="sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="up-sort-select"
              >
                <option value="recent">Más recientes</option>
                <option value="rating">Mejor calificadas</option>
                <option value="oldest">Más antiguas</option>
              </select>
            </div>
          )}
        </div>

        {reviews.length === 0 ? (
          <div className="up-no-reviews">
            <FaStar size={40} style={{ color: 'var(--fin-gold)', marginBottom: '1rem' }} />
            <p>Este trader aún no tiene reseñas. Sé el primero en dejar tu opinión.</p>
          </div>
        ) : (
          <div className="up-reviews-list">
            {getSortedReviews().map((review, idx) => (
              <div key={idx} className="up-review-card">
                <div className="up-review-header">
                  <div className="up-review-user">
                    {review.fromUserAvatar ? (
                      <img
                        src={review.fromUserAvatar}
                        alt={review.fromUserName}
                        className="up-review-avatar"
                      />
                    ) : (
                      <div className="up-review-avatar-placeholder">
                        {review.fromUserName?.slice(0, 2).toUpperCase() || 'U'}
                      </div>
                    )}
                    <div className="up-review-user-info">
                      <span className="up-review-author">@{review.fromUserName}</span>
                      <span className="up-review-date fin-num">
                        {new Date(review.createdAt).toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                  </div>
                  <div className="up-review-stars">
                    {[1, 2, 3, 4, 5].map(i => (
                      <FaStar
                        key={i}
                        size={13}
                        color={i <= review.rating ? getRatingColor(review.rating) : 'var(--mk-border)'}
                      />
                    ))}
                    <span className="up-review-score fin-num" style={{ color: getRatingColor(review.rating) }}>
                      {review.rating}.0
                    </span>
                  </div>
                </div>

                {/* Categories */}
                {(review.communicationRating || review.speedRating || review.accuracyRating) && (
                  <div className="up-review-categories">
                    {review.communicationRating && (
                      <div className="up-review-category">
                        <div className="up-category-mini">
                          <FaComments size={11} style={{ color: '#0091ea' }} />
                          <span>Comunicación</span>
                        </div>
                        <div className="up-category-mini-stars">
                          {[1, 2, 3, 4, 5].map(i => (
                            <FaStar
                              key={i}
                              size={10}
                              color={i <= review.communicationRating ? '#0091ea' : 'var(--mk-border)'}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    {review.speedRating && (
                      <div className="up-review-category">
                        <div className="up-category-mini">
                          <FaBolt size={11} style={{ color: '#f0b90b' }} />
                          <span>Rapidez</span>
                        </div>
                        <div className="up-category-mini-stars">
                          {[1, 2, 3, 4, 5].map(i => (
                            <FaStar
                              key={i}
                              size={10}
                              color={i <= review.speedRating ? '#f0b90b' : 'var(--mk-border)'}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    {review.accuracyRating && (
                      <div className="up-review-category">
                        <div className="up-category-mini">
                          <FaLightbulb size={11} style={{ color: 'var(--fin-live)' }} />
                          <span>Precisión</span>
                        </div>
                        <div className="up-category-mini-stars">
                          {[1, 2, 3, 4, 5].map(i => (
                            <FaStar
                              key={i}
                              size={10}
                              color={i <= review.accuracyRating ? 'var(--fin-live)' : 'var(--mk-border)'}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Review Text */}
                {review.text && (
                  <p className="up-review-text">"{review.text}"</p>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Editor de perfil unificado (cuenta + tienda) — solo el dueño */}
      {editOpen && isOwn && (
        <EditProfile
          initial={user}
          onClose={() => setEditOpen(false)}
          onSaved={(u) => { setUser((prev) => ({ ...prev, ...u })); setEditOpen(false); }}
        />
      )}
    </div>
  );
}
